import { keepPreviousData } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Pencil, Trash2 } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  Button,
  Card,
  Dialog,
  Pagination,
  Skeleton,
  TBody,
  Table,
  Td,
  Th,
  THead,
  Tr,
  useToast,
} from "@rumbledirect/ui-v2";
import { RequirePermission } from "@/currentUser/RequirePermission";
import { useGetAdminUserCollections } from "@/lib/api/generated/user-collection/user-collection";
import { TopbarActions } from "@/shell/TopbarActions";
import type { Team } from "@/teams/team";
import { teamMemberNames, toTeamInput } from "@/teams/teamForm";
import { TeamSheet } from "@/teams/TeamSheet";
import { useCreateTeam } from "@/teams/useCreateTeam";
import { useDeleteTeam } from "@/teams/useDeleteTeam";
import { useUpdateTeam } from "@/teams/useUpdateTeam";

const CREATE_PERMISSION = "admin_user_collection_create";
const UPDATE_PERMISSION = "admin_user_collection_update";
const DELETE_PERMISSION = "admin_user_collection_delete";

const ITEMS_PER_PAGE = 20;
const SKELETON_ROW_KEYS = Array.from(
  { length: 3 },
  (_, index) => `team-skeleton-${index}`,
);

interface TeamsSearch {
  readonly page: number;
}

export const Route = createFileRoute(
  "/_authenticated/affiliate-management_/teams",
)({
  validateSearch: (search: Record<string, unknown>): TeamsSearch => {
    const page = Number(search.page);
    return { page: Number.isInteger(page) && page >= 1 ? page : 1 };
  },
  component: TeamsPage,
});

function TeamsPage(): ReactNode {
  const { page } = Route.useSearch();
  const navigate = Route.useNavigate();
  const toast = useToast();

  const { data, isPending, isPlaceholderData, isError, refetch } =
    useGetAdminUserCollections(
      { page, itemsPerPage: ITEMS_PER_PAGE, "order[name]": "asc" },
      { query: { placeholderData: keepPreviousData } },
    );

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Team | undefined>(undefined);
  const [pendingDelete, setPendingDelete] = useState<Team | undefined>(
    undefined,
  );

  const createMutation = useCreateTeam();
  const updateMutation = useUpdateTeam(editing?.id ?? "");
  const deleteMutation = useDeleteTeam();

  const totalItems = data?.totalItems ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <TopbarActions>
        <RequirePermission permission={CREATE_PERMISSION}>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            Nieuw team
          </Button>
        </RequirePermission>
      </TopbarActions>

      <header>
        <Link
          to="/affiliate-management"
          className="mb-3 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Organisatiebeheer
        </Link>
        <h1 className="text-2xl font-semibold tracking-tight">Teams</h1>
      </header>

      <Card className="overflow-hidden">
        <Table>
          <THead>
            <Tr>
              <Th className="w-64">Naam</Th>
              <Th>Leden</Th>
              <Th className="w-24 text-right">
                <span className="sr-only">Acties</span>
              </Th>
            </Tr>
          </THead>
          <TBody
            aria-busy={isPlaceholderData}
            className={isPlaceholderData ? "opacity-60" : undefined}
          >
            <TeamRows
              isPending={isPending}
              isError={isError}
              teams={data?.member}
              onEdit={setEditing}
              onDelete={setPendingDelete}
              onRetry={() => void refetch()}
            />
          </TBody>
        </Table>
      </Card>

      {totalItems > ITEMS_PER_PAGE && (
        <Pagination
          page={page}
          pageSize={ITEMS_PER_PAGE}
          totalItems={totalItems}
          status={isPending ? "pending" : isError ? "error" : "ready"}
          onPageChange={(next) =>
            void navigate({ search: (prev) => ({ ...prev, page: next }) })
          }
        />
      )}

      <TeamSheet
        open={createOpen}
        onOpenChange={setCreateOpen}
        isSubmitting={createMutation.isPending}
        onSubmit={(values) => {
          createMutation.mutate(toTeamInput(values), {
            onSuccess: () => setCreateOpen(false),
          });
        }}
      />

      <TeamSheet
        open={editing !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditing(undefined);
        }}
        team={editing}
        isSubmitting={updateMutation.isPending}
        onSubmit={(values) => {
          updateMutation.mutate(toTeamInput(values), {
            onSuccess: () => setEditing(undefined),
          });
        }}
      />

      <Dialog
        open={pendingDelete !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(undefined);
        }}
        title="Team verwijderen"
        description={
          pendingDelete
            ? `Weet je zeker dat je "${pendingDelete.name}" wilt verwijderen? Dit kan niet ongedaan worden gemaakt.`
            : undefined
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setPendingDelete(undefined)}
              disabled={deleteMutation.isPending}
            >
              Annuleren
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                const id = pendingDelete?.id;
                if (!id) {
                  toast.error("Verwijderen is mislukt. Probeer het opnieuw.");
                  return;
                }
                deleteMutation.mutate(id, {
                  onSuccess: () => setPendingDelete(undefined),
                });
              }}
            >
              Team verwijderen
            </Button>
          </>
        }
      >
        <div
          role="alert"
          className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-foreground"
        >
          <p className="font-medium">
            Dit gebeurt er met het werk van dit team:
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {pendingDelete && pendingDelete.members.length > 0 && (
              <li>
                {pendingDelete.members.length === 1
                  ? "1 lid wordt uit het team gehaald."
                  : `${pendingDelete.members.length} leden worden uit het team gehaald.`}
              </li>
            )}
            <li>
              Dossiers en taken van dit team hebben daarna geen team meer. Werk
              dat ook niet aan een behandelaar is toegewezen, staat bij niemand
              meer in de werkvoorraad.
            </li>
            <li>Taakregels maken voortaan taken zonder dit team aan.</li>
          </ul>
        </div>
      </Dialog>
    </div>
  );
}

function TeamRows({
  isPending,
  isError,
  teams,
  onEdit,
  onDelete,
  onRetry,
}: {
  readonly isPending: boolean;
  readonly isError: boolean;
  readonly teams: readonly Team[] | undefined;
  readonly onEdit: (team: Team) => void;
  readonly onDelete: (team: Team) => void;
  readonly onRetry: () => void;
}): ReactNode {
  if (isPending) {
    return SKELETON_ROW_KEYS.map((key) => (
      <Tr key={key}>
        <Td>
          <Skeleton className="h-4 w-32" />
        </Td>
        <Td>
          <Skeleton className="h-4 w-64" />
        </Td>
        <Td>
          <div className="flex justify-end gap-1">
            <Skeleton className="h-8 w-8" />
            <Skeleton className="h-8 w-8" />
          </div>
        </Td>
      </Tr>
    ));
  }

  if (isError) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center">
          <div role="alert" className="space-y-3">
            <p className="text-muted-foreground">
              We konden de teams niet laden.
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              Opnieuw proberen
            </Button>
          </div>
        </Td>
      </Tr>
    );
  }

  if (teams === undefined || teams.length === 0) {
    return (
      <Tr>
        <Td colSpan={3} className="py-10 text-center text-muted-foreground">
          Nog geen teams.
        </Td>
      </Tr>
    );
  }

  return teams.map((team) => (
    <Tr key={team.id}>
      <Td className="font-medium">{team.name}</Td>
      <Td className="text-muted-foreground">
        {team.members.length === 0 ? "Geen leden" : teamMemberNames(team)}
      </Td>
      <Td>
        <div className="flex justify-end gap-1">
          <RequirePermission permission={UPDATE_PERMISSION}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${team.name} bewerken`}
              onClick={() => onEdit(team)}
            >
              <Pencil className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
          <RequirePermission permission={DELETE_PERMISSION}>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label={`${team.name} verwijderen`}
              onClick={() => onDelete(team)}
            >
              <Trash2 className="size-4" aria-hidden="true" />
            </Button>
          </RequirePermission>
        </div>
      </Td>
    </Tr>
  ));
}
